import {
  ArnAuthContext,
  ArnAuthStatus,
  NoAuthContextError,
} from '@arianee/arn-client';
import { ObjectUtil } from '@arianee/arn-types';
import { ArnIfConnectedComponent } from '../if-connected/ArnIfConnectedComponent';
import { ArnClientProvider } from '../ArnComponent';

export enum ArnConnectEventType {
  error = 'error',
  connected = 'connected',
  connecting = 'connecting',
  disconnected = 'disconnected',
}

export class ArnConnectEvent<D> extends CustomEvent<D> {
  constructor(type: ArnConnectEventType, detail?: D) {
    super(type, {
      composed: true,
      bubbles: true,
      detail,
    });
  }
}

export class ArnDisconnectedEvent extends ArnConnectEvent<ArnAuthStatus> {
  static type = ArnConnectEventType.disconnected;

  constructor() {
    super(ArnDisconnectedEvent.type);
  }
}

export class ArnConnectingEvent extends ArnConnectEvent<ArnAuthStatus> {
  static type = ArnConnectEventType.connecting;

  constructor() {
    super(ArnConnectingEvent.type);
  }
}

export class ArnConnectedEvent extends ArnConnectEvent<ArnAuthContext> {
  static type = ArnConnectEventType.connected;

  constructor(readonly context: ArnAuthContext) {
    super(ArnConnectedEvent.type, context);
  }
}

export class ArnErrorEvent extends ArnConnectEvent<Error> {
  static type = ArnConnectEventType.error;

  constructor(readonly error: Error) {
    super(ArnErrorEvent.type, error);
  }
}

/**
 * Wallet connection widget.
 *
 * @param not-connected slot to put UI to connect when clicked.
 * @param connected slot to put UI to disconnect when clicked.
 * @see EVENT_ERROR
 * @see EVENT_CONNECTED
 * @see EVENT_CONNECTING
 * @see EVENT_DISCONNECTED
 */
export class ArnConnectComponent extends ArnIfConnectedComponent {
  constructor(arnClientProvider?: ArnClientProvider) {
    super(arnClientProvider);
  }

  override connectedCallback() {
    super.connectedCallback();
    this.falseElem.addEventListener('click', () => this.connect());
    this.trueElem.addEventListener('click', () => this.disconnect());
  }

  async connect() {
    this.dispatchEvent(new ArnConnectingEvent());
    try {
      const context = (this.context = await this.arnClient.auth.connect());
      this.updateStatus(context.status);
      this.dispatchEvent(new ArnConnectedEvent(context));
    } catch (e) {
      this.dispatchEvent(new ArnErrorEvent(e as Error));
      this.dispatchEvent(new ArnDisconnectedEvent());
      throw e;
    }
  }

  async disconnect(): Promise<void> {
    const ctx = ObjectUtil.asSet(this.context, () => new NoAuthContextError());
    await ctx.disconnect();
    this.updateStatus(ctx.status);
    this.dispatchEvent(new ArnDisconnectedEvent());
  }
}

const name = 'arn-connect';
if (!window.customElements.get(name)) {
  window.customElements.define(name, ArnConnectComponent);
}
